import { useEffect, useState } from 'react';

/**
 * Noticing that the app on the screen is no longer the app on the server.
 *
 * A deploy replaces the hashed bundles and deletes the old ones. A tab that
 * was already open keeps running code that no longer exists at the other end:
 * it works until it asks for anything new, then breaks in ways that read as
 * our bugs — a blank screen, or a route that never loads.
 *
 * The build stamps its own id into the bundle and next to it as version.json.
 * Comparing the two is the whole mechanism.
 */

declare const __BUILD_ID__: string;

/** What this running copy was built as. */
export const RUNNING_VERSION = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    // no-store, or the check itself is answered from the cache it exists to
    // see past.
    const response = await fetch(`${import.meta.env.BASE_URL}version.json?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) return null;
    const payload = await response.json();
    return typeof payload?.version === 'string' ? payload.version : null;
  } catch {
    // Offline, or a gateway having a moment. Not evidence of a new release.
    return null;
  }
}

/**
 * True once the server is serving something newer than this tab.
 *
 * Checked on an interval and whenever the tab is brought back to the front,
 * which is when someone returning from lunch is about to click on something.
 */
export function useNewVersionAvailable(): boolean {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (RUNNING_VERSION === 'dev') return;
    let cancelled = false;

    const check = async () => {
      const deployed = await fetchDeployedVersion();
      if (!cancelled && deployed && deployed !== RUNNING_VERSION) setStale(true);
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };

    void check();
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return stale;
}
